/**
 * POLISH RENAME (shared cascade helper)
 *
 * Rewrites a polish's identity across every collection that references
 * it by name or slug — used by both the single-polish admin tool
 * (MergePolishes.tsx) and the bulk PolishNamesCleanup page.
 *
 * A polish's identity spans more storage than any other item field on
 * the app, so a naive rewrite leaks. This helper touches every site:
 *
 *   1. `users/{uid}/items/{id}`   — brand, name, slug, brandLower,
 *                                    nameLower, brandSlug, nameSlug
 *                                    (all seven — via `itemDocKeys`).
 *   2. `polishConfirms/{slug}__{uid}` — migrated from old slug to new;
 *                                    the doc's `slug` payload is
 *                                    rewritten too. Duplicate confirms
 *                                    (same user, already confirmed the
 *                                    target) collapse to one — set-then-
 *                                    delete is idempotent.
 *   3. `users/{uid}/creations/{cid}` — each creation whose
 *                                    itemsUsedRefs contains the old ref
 *                                    gets its itemsUsed[i].brand/name
 *                                    rewritten AND its itemsUsedRefs[]
 *                                    fully recomputed via the shared
 *                                    `itemsUsedRefs()` helper (which
 *                                    dedupes automatically).
 *   4. `polishCatalog/{slug}`     — NOT touched here. The
 *                                    `onUserPolishWrite` Firestore
 *                                    trigger sees each item's slug
 *                                    change in step 1, deletes the old
 *                                    catalog doc when it goes empty,
 *                                    and rebuilds the new one. Free.
 *                                    A belt-and-suspenders explicit
 *                                    deleteDoc is included at the end
 *                                    to close the "visible immediately"
 *                                    UX gap MergePolishes.tsx used to
 *                                    provide.
 *
 * `dryRun: true` reports the counts each step WOULD write without
 * committing. Item + confirm + creation walks each happen regardless
 * so the summary is honest.
 *
 * Brand catalog items at `brands/{brandId}/items/...` are deliberately
 * NOT touched — verified brand product records live under
 * brand-management, and their own trigger keeps them aligned.
 */

import {
  collectionGroup,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  writeBatch,
  deleteDoc,
  serverTimestamp,
  type QueryDocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { polishSlug } from '../utils/polishSlug';
import { itemDocKeys } from '../utils/itemDocKeys';
import {
  itemsUsedRefs as computeItemsUsedRefs,
  itemUsedRef,
} from '../utils/creationRefs';

/** Firestore commit cap is 500 writes per batch — pad to 450 for headroom
 *  against downstream trigger writes racing our commits. Matches the
 *  batch limit used across the cleanup admin pages. */
const BATCH_LIMIT = 450;

export interface RenamePolishInput {
  fromBrand: string;
  fromName: string;
  toBrand: string;
  toName: string;
  dryRun?: boolean;
  /** Optional callback for streaming progress lines to a caller's log
   *  panel. Called at the end of each step with a short human message. */
  onLog?: (message: string) => void;
}

export interface RenamePolishSummary {
  dryRun: boolean;
  fromSlug: string;
  toSlug: string;
  itemsScanned: number;
  itemsMatched: number;
  itemsUpdated: number;
  confirmsScanned: number;
  confirmsMigrated: number;
  creationsScanned: number;
  creationsUpdated: number;
  creationEntriesRewritten: number;
  catalogDocDeleted: boolean;
}

export async function renamePolish(
  input: RenamePolishInput
): Promise<RenamePolishSummary> {
  const dryRun = input.dryRun === true;
  const onLog = input.onLog ?? (() => {});

  const fromBrand = input.fromBrand.trim();
  const fromName = input.fromName.trim();
  const toBrand = input.toBrand.trim();
  const toName = input.toName.trim();

  if (!fromBrand || !fromName || !toBrand || !toName) {
    throw new Error(
      'renamePolish requires non-empty fromBrand, fromName, toBrand, toName.'
    );
  }

  const fromSlug = polishSlug(fromBrand, fromName);
  const toSlug = polishSlug(toBrand, toName);
  if (fromSlug === toSlug) {
    throw new Error(
      `renamePolish: fromSlug and toSlug are identical ("${fromSlug}") — nothing to rename.`
    );
  }

  const fromBrandLower = fromBrand.toLowerCase();
  const fromNameLower = fromName.toLowerCase();
  const toKeys = itemDocKeys(toBrand, toName);
  const fromKeys = itemDocKeys(fromBrand, fromName);
  const fromItemRef = itemUsedRef({
    itemType: 'polish',
    brand: fromBrand,
    name: fromName,
  });

  onLog(`Renaming polish "${fromBrand} — ${fromName}" → "${toBrand} — ${toName}"`);
  onLog(`  fromSlug: ${fromSlug}`);
  onLog(`  toSlug:   ${toSlug}`);

  // ============================================================
  // STEP 1 — User items
  // ============================================================
  // Match by slug when present (canonical id), and by brand+name
  // (case-insensitive) as a fallback for legacy items that predate
  // Phase 1's slug backfill. Deduped by document path.
  const [bySlugSnap, byBrandSnap] = await Promise.all([
    getDocs(query(collectionGroup(db, 'items'), where('slug', '==', fromSlug))),
    getDocs(
      query(
        collectionGroup(db, 'items'),
        where('brandLower', '==', fromBrandLower)
      )
    ),
  ]);

  const itemCandidates = new Map<string, QueryDocumentSnapshot<DocumentData>>();
  for (const d of bySlugSnap.docs) itemCandidates.set(d.ref.path, d);
  for (const d of byBrandSnap.docs) {
    if (itemCandidates.has(d.ref.path)) continue;
    const data = d.data() as Record<string, unknown>;
    const nameLower =
      typeof data.nameLower === 'string'
        ? data.nameLower
        : typeof data.name === 'string'
        ? data.name.trim().toLowerCase()
        : '';
    if (nameLower === fromNameLower) itemCandidates.set(d.ref.path, d);
  }

  let itemsScanned = itemCandidates.size;
  let itemsMatched = 0;
  let itemsUpdated = 0;
  let itemsBatch = writeBatch(db);
  let itemsBatchOps = 0;

  for (const d of itemCandidates.values()) {
    // Restrict to user items — verified brand product records at
    // brands/{brandId}/items are managed elsewhere and must not be
    // rewritten from here.
    if (d.ref.parent.parent?.parent?.id !== 'users') continue;
    itemsMatched++;
    const patch = {
      brand: toBrand,
      name: toName,
      slug: toSlug,
      ...toKeys,
    };
    if (!dryRun) itemsBatch.update(d.ref, patch);
    itemsUpdated++;
    itemsBatchOps++;
    if (itemsBatchOps >= BATCH_LIMIT) {
      if (!dryRun) await itemsBatch.commit();
      itemsBatch = writeBatch(db);
      itemsBatchOps = 0;
    }
  }
  if (itemsBatchOps > 0 && !dryRun) await itemsBatch.commit();
  onLog(
    `  Items: scanned ${itemsScanned}, matched ${itemsMatched}, ${dryRun ? 'would-update' : 'updated'} ${itemsUpdated}.`
  );

  // ============================================================
  // STEP 2 — polishConfirms migration
  // ============================================================
  // Confirms are id'd `{slug}__{uid}`. Move each to the new id and
  // rewrite the doc's `slug` payload. If the user already had a
  // confirm on the target slug, our setDoc collapses to one (same
  // canonical confirmation) and we still delete the old — net effect
  // is one confirm per user on the canonical polish.
  const confirmsSnap = await getDocs(
    query(collection(db, 'polishConfirms'), where('slug', '==', fromSlug))
  );
  const confirmsScanned = confirmsSnap.size;
  let confirmsMigrated = 0;
  // polishConfirms are top-level docs; batched writes still bounded.
  let confirmsBatch = writeBatch(db);
  let confirmsBatchOps = 0;

  for (const d of confirmsSnap.docs) {
    const data = d.data() as Record<string, unknown>;
    const userId =
      typeof data.userId === 'string' ? data.userId : d.id.split('__')[1];
    if (!userId) continue;
    const newRef = doc(db, 'polishConfirms', `${toSlug}__${userId}`);
    if (!dryRun) {
      confirmsBatch.set(newRef, {
        slug: toSlug,
        userId,
        // Preserve createdAt if it exists, otherwise stamp now.
        createdAt: data.createdAt ?? serverTimestamp(),
      });
      confirmsBatch.delete(d.ref);
    }
    confirmsMigrated++;
    // Each migration is 2 ops (set + delete); commit at half the cap.
    confirmsBatchOps += 2;
    if (confirmsBatchOps >= BATCH_LIMIT) {
      if (!dryRun) await confirmsBatch.commit();
      confirmsBatch = writeBatch(db);
      confirmsBatchOps = 0;
    }
  }
  if (confirmsBatchOps > 0 && !dryRun) await confirmsBatch.commit();
  onLog(
    `  Confirms: ${dryRun ? 'would-migrate' : 'migrated'} ${confirmsMigrated} of ${confirmsScanned} scanned.`
  );

  // ============================================================
  // STEP 3 — Creations rewriting
  // ============================================================
  // Any creation with the old ref in its itemsUsedRefs[] has a stale
  // snapshot in itemsUsed[i]. Rewrite both: the object entry
  // (brand+name change) and the derived refs array (fully recomputed
  // via the shared helper — dedupe included).
  const creationsSnap = await getDocs(
    query(
      collectionGroup(db, 'creations'),
      where('itemsUsedRefs', 'array-contains', fromItemRef)
    )
  );
  let creationsScanned = 0;
  let creationsUpdated = 0;
  let creationEntriesRewritten = 0;
  let creationsBatch = writeBatch(db);
  let creationsBatchOps = 0;

  for (const d of creationsSnap.docs) {
    // Restrict to user-owned creations (users/{uid}/creations/{id}).
    if (d.ref.parent.parent?.parent?.id !== 'users') continue;
    creationsScanned++;
    const data = d.data() as Record<string, unknown>;
    if (data.isDeleted === true) continue;
    const itemsUsed = Array.isArray(data.itemsUsed)
      ? (data.itemsUsed as Array<Record<string, unknown>>)
      : [];

    let anyChange = false;
    const updatedItemsUsed = itemsUsed.map((entry) => {
      const b = typeof entry.brand === 'string' ? entry.brand.trim() : '';
      const n = typeof entry.name === 'string' ? entry.name.trim() : '';
      const t = typeof entry.itemType === 'string' ? entry.itemType : 'polish';
      // Match on itemType + brand+name slugs so a creation whose entry
      // is missing the raw fields but present in refs still hits.
      const entryRef = itemUsedRef({ itemType: t, brand: b, name: n });
      if (entryRef === fromItemRef) {
        anyChange = true;
        creationEntriesRewritten++;
        return { ...entry, brand: toBrand, name: toName };
      }
      return entry;
    });

    if (!anyChange) continue;

    const newRefs = computeItemsUsedRefs(
      updatedItemsUsed.map((e) => ({
        itemType: typeof e.itemType === 'string' ? e.itemType : 'polish',
        brand: typeof e.brand === 'string' ? e.brand : '',
        name: typeof e.name === 'string' ? e.name : '',
      }))
    );

    if (!dryRun) {
      creationsBatch.update(d.ref, {
        itemsUsed: updatedItemsUsed,
        itemsUsedRefs: newRefs,
      });
    }
    creationsUpdated++;
    creationsBatchOps++;
    if (creationsBatchOps >= BATCH_LIMIT) {
      if (!dryRun) await creationsBatch.commit();
      creationsBatch = writeBatch(db);
      creationsBatchOps = 0;
    }
  }
  if (creationsBatchOps > 0 && !dryRun) await creationsBatch.commit();
  onLog(
    `  Creations: scanned ${creationsScanned}, ${dryRun ? 'would-update' : 'updated'} ${creationsUpdated} (${creationEntriesRewritten} entries).`
  );

  // ============================================================
  // STEP 4 — polishCatalog (belt-and-suspenders)
  // ============================================================
  // The onUserPolishWrite trigger will delete the old catalog doc
  // automatically once step 1's items no longer point at it. Doing it
  // explicitly here just makes the merge visible sooner in the UI. If
  // the doc is already gone (trigger raced ahead of us) the deleteDoc
  // is a no-op.
  let catalogDocDeleted = false;
  if (!dryRun) {
    try {
      const catalogSnap = await getDoc(doc(db, 'polishCatalog', fromSlug));
      if (catalogSnap.exists()) {
        await deleteDoc(doc(db, 'polishCatalog', fromSlug));
        catalogDocDeleted = true;
        onLog(`  Catalog: deleted stale doc polishCatalog/${fromSlug}.`);
      }
    } catch (err) {
      onLog(
        `  Catalog: could not delete stale doc — ${(err as Error).message}`
      );
    }
  }

  // Reference `fromKeys` so linters see the fallback matcher we
  // computed above is intentional (used inside the byBrandLower query
  // path — the object is here for future callers that want to also
  // match by brandSlug/nameSlug directly).
  void fromKeys;

  return {
    dryRun,
    fromSlug,
    toSlug,
    itemsScanned,
    itemsMatched,
    itemsUpdated,
    confirmsScanned,
    confirmsMigrated,
    creationsScanned,
    creationsUpdated,
    creationEntriesRewritten,
    catalogDocDeleted,
  };
}
